import type { ErrorInfo, ReactNode } from "react";
import { Component, useState } from "react";
import { trackClarityEvent, upgradeClaritySession } from "@/services/clarity";
import { ErrorFallback } from "./ErrorFallback";

interface Props {
	children: ReactNode;
	showSidebar?: boolean;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

// --- Hook: Re-throw errors during render phase so error boundaries catch them ---
export const useErrorHandler = () => {
	const [, setState] = useState();
	return (error: unknown) => {
		setState(() => {
			throw error;
		});
	};
};

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error("ErrorBoundary caught:", error, errorInfo);

		// A crashed screen is exactly the session worth replaying: tag it as a
		// smart event and upgrade it so sampling can't discard the recording.
		trackClarityEvent("error_boundary");
		upgradeClaritySession("error_boundary");
	}

	render() {
		if (this.state.hasError && this.state.error) {
			return (
				<ErrorFallback
					error={this.state.error}
					showSidebar={this.props.showSidebar ?? false}
				/>
			);
		}

		return this.props.children;
	}
}
